/**
 * The DIME identity seam: how a host turns its own session credential into a
 * DIME JWT (the credential every web-api call and gateway-proxy handshake
 * presents).
 *
 * The pluggable unit is the token EXCHANGE, not the sign-in provider —
 * everything downstream of minting (401 re-mint retry, sidecar auth cache,
 * handshake token resolver, connection state machine) only ever consumes
 * minted `DimeIdentity` values. Clerk is one implementation
 * (`createClerkTokenExchange`); a future Paradex/Paradigm exchange (host JWT →
 * DIME JWT via a new web-api /auth endpoint) slots in with zero changes
 * downstream.
 *
 * Never log host tokens or the DIME JWT.
 */

export class DimeApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * Machine-readable code from the web-api's error envelope (exact strings
     * — the host-integration contract), when the response carried one:
     * `IDENTITY_NOT_LINKED`, `IDENTITY_ALREADY_LINKED`,
     * `ACCOUNT_NOT_ELIGIBLE`, `LINK_LIMIT_REACHED`.
     */
    readonly code?: string,
  ) {
    super(message);
    this.name = 'DimeApiError';
  }
}

/**
 * The exchange refused a VERIFIED platform token because the identity has no
 * account link. The cue to fall back to Clerk and offer linking afterwards —
 * anything else (401, network) is a plain failure with no linking story.
 */
export const IDENTITY_NOT_LINKED = 'IDENTITY_NOT_LINKED';
/** The identity already has an active link (here or on another account). */
export const IDENTITY_ALREADY_LINKED = 'IDENTITY_ALREADY_LINKED';
/** Linking requires an onboarded, active DIME account. */
export const ACCOUNT_NOT_ELIGIBLE = 'ACCOUNT_NOT_ELIGIBLE';
/** The per-(account, provider) active-link cap was reached. */
export const LINK_LIMIT_REACHED = 'LINK_LIMIT_REACHED';

/** A freshly minted DIME JWT plus the routing claims decoded from it. */
export interface DimeIdentity {
  /** The DIME JWT itself (5-minute TTL — use immediately, never store). */
  readonly token: string;
  /** Routes the UI: true ⇒ resolve the connection, false ⇒ access-code screen. */
  readonly onboarded: boolean;
}

/**
 * How this host obtains a DIME JWT. Called immediately before every request
 * that needs one (the JWT's 5-minute TTL means it is never stored or reused
 * across calls beyond the short caches in the client). Implementations throw
 * `DimeApiError` on failure.
 *
 * `apiUrl` is the resolved web-api base (no trailing slash) so an exchange
 * targets the same environment as the client that invokes it.
 */
export type DimeTokenExchange = (apiUrl: string) => Promise<DimeIdentity>;

/**
 * Base64url-decode a JWT's payload segment. Decode only — the backend signed
 * it moments ago over TLS; client-side signature verification adds nothing.
 */
export function decodeJwtPayload(
  token: string,
): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1].replace(/-/gu, '+').replace(/_/gu, '/'));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Best-effort read of the web-api error envelope's machine code. Never
 * throws — a non-JSON body just yields undefined.
 */
async function readErrorCode(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { code?: unknown };
    return typeof body.code === 'string' ? body.code : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The shared exchange core: POST an identity token (Clerk session JWT,
 * Paradex terminal token, Paradigm embed token — the web-api dispatches by
 * issuer) to /v1/auth/identity and return the minted DimeIdentity. Throws
 * DimeApiError carrying the envelope's machine code when present.
 */
async function exchangeIdentityToken(
  apiUrl: string,
  identityToken: string,
): Promise<DimeIdentity> {
  const res = await fetch(`${apiUrl}/v1/auth/identity`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: identityToken }),
  });
  if (!res.ok) {
    throw new DimeApiError(
      res.status,
      `Identity exchange failed (${res.status})`,
      await readErrorCode(res),
    );
  }
  const body = (await res.json()) as { token?: string };
  if (typeof body.token !== 'string' || body.token.length === 0) {
    throw new DimeApiError(res.status, 'Identity exchange returned no token');
  }
  const payload = decodeJwtPayload(body.token);
  return {
    token: body.token,
    onboarded: payload?.onboarded === true,
  };
}

/**
 * The Clerk exchange: swap the current Clerk session token for a fresh DIME
 * JWT via POST /v1/auth/identity. `getToken` is Clerk's `useAuth().getToken`
 * — it resolves null when the session has ended.
 */
export function createClerkTokenExchange(
  getToken: () => Promise<string | null>,
): DimeTokenExchange {
  return async (apiUrl) => {
    const clerkToken = await getToken();
    if (clerkToken == null || clerkToken.length === 0) {
      throw new DimeApiError(401, 'No Clerk session token available');
    }
    return exchangeIdentityToken(apiUrl, clerkToken);
  };
}

/**
 * The platform (native host auth) exchange, for `DimeCloudApp`'s
 * `silentExchange` seam: mint a fresh platform token from the host, swap it
 * for a DIME JWT. Works for any platform the web-api trusts — the token is
 * opaque here (Paradex: POST /v1/account/terminal-token → `jwt_token`;
 * Paradigm: GET /account/embed-token/?target=dime → `token`).
 *
 * `mintPlatformToken` is called on EVERY invocation — the exchange runs
 * before each request that needs a DIME JWT, and platform tokens are
 * 5-minute attestations that must never be cached.
 *
 * `onNotLinked` fires when the token VERIFIED but the identity has no
 * account link (the web-api's IDENTITY_NOT_LINKED refusal) — the host's cue
 * to offer "link your account" after the Clerk fallback signs the user in
 * (see `renderLinkPrompt`). Other failures are plain rejections.
 */
export function createPlatformTokenExchange(
  mintPlatformToken: () => Promise<string | null>,
  options?: { readonly onNotLinked?: () => void },
): DimeTokenExchange {
  return async (apiUrl) => {
    const platformToken = await mintPlatformToken();
    if (platformToken == null || platformToken.length === 0) {
      throw new DimeApiError(401, 'No platform token available');
    }
    try {
      return await exchangeIdentityToken(apiUrl, platformToken);
    } catch (err) {
      if (err instanceof DimeApiError && err.code === IDENTITY_NOT_LINKED) {
        options?.onNotLinked?.();
      }
      throw err;
    }
  };
}

/**
 * Sever the active link for a platform identity with ONLY the platform token
 * as authority: POST /v1/identity/links/self-unlink. No DIME JWT — proof of
 * control of the platform identity is sufficient to stop that identity's
 * sessions landing in the linked account. Resolves whether or not a link
 * existed (the server never discloses that to unauthenticated callers);
 * rejects with DimeApiError for an invalid/expired platform token.
 */
export async function selfUnlinkPlatformIdentity(
  apiUrl: string,
  platformToken: string,
): Promise<void> {
  const res = await fetch(`${apiUrl}/v1/identity/links/self-unlink`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: platformToken }),
  });
  if (!res.ok) {
    throw new DimeApiError(
      res.status,
      `Self-unlink failed (${res.status})`,
      await readErrorCode(res),
    );
  }
}

/** An active platform-identity → account link, as the web-api returns it. */
export interface IdentityLink {
  readonly id: string;
  /** Platform label, e.g. "paradex" / "paradigm" / "paradigm-staging". */
  readonly provider: string;
  /** Canonicalized platform-side id (address / numeric user id). */
  readonly subject: string;
  /** Display-only label captured at link time (email when available). */
  readonly displayName?: string;
  readonly linkedAt?: string;
}

/**
 * Link a verified platform identity to the caller's DIME account:
 * POST /v1/identity/links with a freshly minted DIME JWT (via `exchange`,
 * the Clerk one in practice) and the host-minted platform token as the body.
 * Both credentials are proofs — the platform token of controlling the
 * platform identity, the DIME JWT of owning the account; nothing is
 * client-asserted. Rejects with a coded DimeApiError:
 * IDENTITY_ALREADY_LINKED (409), ACCOUNT_NOT_ELIGIBLE (403),
 * LINK_LIMIT_REACHED (422), or 401 for an invalid/expired platform token.
 */
export async function linkPlatformIdentity(
  apiUrl: string,
  exchange: DimeTokenExchange,
  platformToken: string,
): Promise<IdentityLink> {
  const { token } = await exchange(apiUrl);
  const res = await fetch(`${apiUrl}/v1/identity/links`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ token: platformToken }),
  });
  if (!res.ok) {
    throw new DimeApiError(
      res.status,
      `Identity link failed (${res.status})`,
      await readErrorCode(res),
    );
  }
  const body = (await res.json()) as {
    id?: string;
    provider?: string;
    subject?: string;
    display_name?: string;
    linked_at?: string;
  };
  if (typeof body.id !== 'string' || typeof body.provider !== 'string') {
    throw new DimeApiError(res.status, 'Identity link returned no link');
  }
  return {
    id: body.id,
    provider: body.provider,
    subject: body.subject ?? '',
    displayName: body.display_name,
    linkedAt: body.linked_at,
  };
}
